import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { ArrowDown, ArrowUp } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import SheetDrawerWrapper, {
  SheetDrawerBody,
  SheetDrawerFooter,
} from '@/components/ui/sheet-drawer';
import DataSources from 'components/DisasterGPT/DataSources';
import { useRefreshSitrepV2 } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { DEFAULT_SEERIST_REQUEST } from 'components/DisasterGPT/SeeristRequestBuilder';
import {
  clampOpordTasksActionCount,
  DEFAULT_OPORD_TASKS_ACTION_COUNT,
  getDefaultAiInstructionsForSection,
  isOpordExecutionParentSection,
  isOpordTasksSection,
  MAX_OPORD_TASKS_ACTION_COUNT,
  MIN_OPORD_TASKS_ACTION_COUNT,
} from 'components/DisasterGPT/constants/opordSectionDefaults';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import { resolveEffectiveDataSources } from 'components/Reports/document/sitrepDataSourceCount';
import {
  buildAiTasksSettings,
  DEFAULT_AI_TASK_INSTRUCTIONS,
  describeAutomatedProvider,
} from 'components/DisasterGPT/automatedSectionSettings';

const SECTION_SETTINGS_HIDDEN_SOURCES = [
  'Current Weather',
  'Forecast Weather',
  'Power Outages',
  'ERI (Energy Reliability Index)',
  'NEO Analysis',
];
import ReportSectionSourcesPicker from 'components/Reports/section/ReportSectionSourcesPicker';
import TaskSectionFilterFields from 'components/Reports/tasks/TaskSectionFilterFields';
import {
  LEGACY_TASK_SECTION_FILTER,
  readTaskSectionFilter,
  type TaskSectionFilter,
} from 'components/Reports/tasks/taskSectionFilter';
import {
  defaultTextSectionDataSources,
  type DefaultSourcesSection,
} from 'components/Reports/section/defaultTextSectionSources';
import type { ReportsLayoutMode } from 'components/Reports/hooks/useReportsLayoutMode';
import { useHighSecurityFlag } from 'utils/featureFlags';

type SectionType = 'manual' | 'ai' | 'automated';

type SectionFormValues = {
  id: string;
  name: string;
  type: SectionType;
  dataSources: unknown[];
  ai_instructions: string;
  outputFormats: string[];
  action_count: number;
  autoRefreshMinutes: number;
  aiTasksEnabled: boolean;
  aiTasksDataSources: unknown[];
  aiTasksInstructions: string;
  aiTasksActionCount: number;
  taskFilter: TaskSectionFilter;
};

const AUTO_REFRESH_OPTIONS = [0, 5, 15, 30, 60] as const;

type SitrepSectionLike = {
  id?: string;
  name?: string;
  type?: string;
  data_sources?: unknown[];
  ai_instructions?: string;
  output_format?: string;
  output_format_structured?: {
    summary_options?: string[];
    action_count?: number;
    automation?: { provider?: string; [key: string]: unknown };
    auto_refresh?: {
      enabled?: boolean;
      intervalMinutes?: number;
    };
    ai_tasks?: {
      enabled?: boolean;
      data_sources?: unknown[];
      instructions?: string;
      action_count?: number;
    };
    [key: string]: unknown;
  };
  automation?: { provider?: string; [key: string]: unknown };
  section_template_id?: string | null;
  parent_section_id?: string | null;
};

type SaveSectionPayload = {
  id: string;
  name: string;
  type: string;
  data_sources: string[] | Record<string, unknown>[];
  ai_instructions: string;
  automation?: unknown;
  output_format_structured: Record<string, unknown>;
};

type SitrepSectionEditDrawerProps = {
  show?: boolean;
  onHide: () => void;
  onSave: (sectionData: SaveSectionPayload) => Promise<void> | void;
  onDelete?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  section: SitrepSectionLike;
  sitrepId?: string;
  sitrepTemplateId?: string | null;
  parentDataSources?: unknown;
  /**
   * All sections of the report; enables "Other sections in this report" as AI
   * data sources.
   */
  reportSections?: DefaultSourcesSection[];
  layoutMode?: ReportsLayoutMode;
};

const SitrepSectionEditDrawer: React.FC<SitrepSectionEditDrawerProps> = ({
  onHide,
  onSave,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  section,
  sitrepId,
  sitrepTemplateId,
  parentDataSources,
  reportSections,
  layoutMode = 'tabs',
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDirtyClose, setShowDirtyClose] = useState(false);
  const isAutomatedSection = section?.type === 'automated';
  const isLayerStatsAutomated =
    isAutomatedSection &&
    (section?.automation?.provider === 'layerStats' ||
      section?.output_format_structured?.automation?.provider === 'layerStats');
  const isTasksSection = isOpordTasksSection({
    sectionName: section?.name,
    outputFormat: section?.output_format,
    sitrepTemplateId,
  });
  const automatedProvider =
    section?.automation?.provider ??
    section?.output_format_structured?.automation?.provider;
  const isActionsAutomated =
    isAutomatedSection && automatedProvider === 'actions';

  const methods = useForm<SectionFormValues>({
    mode: 'onChange',
    defaultValues: {
      id: '',
      name: '',
      type: 'ai',
      dataSources: [],
      ai_instructions: '',
      outputFormats: [],
      action_count: DEFAULT_OPORD_TASKS_ACTION_COUNT,
      autoRefreshMinutes: 0,
      aiTasksEnabled: false,
      aiTasksDataSources: [],
      aiTasksInstructions: '',
      aiTasksActionCount: DEFAULT_OPORD_TASKS_ACTION_COUNT,
      taskFilter: LEGACY_TASK_SECTION_FILTER,
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    control,
    formState: { isDirty },
  } = methods;
  const selectedType = watch('type');
  const dataSources = useWatch({ control, name: 'dataSources' }) || [];
  const aiTasksEnabled = useWatch({ control, name: 'aiTasksEnabled' });
  const aiTasksDataSources =
    useWatch({ control, name: 'aiTasksDataSources' }) || [];
  const refreshSitrepMutation = useRefreshSitrepV2();
  const highSecurity = useHighSecurityFlag();
  const defaultSourcesInputRef = useRef({ reportSections, highSecurity });
  useEffect(() => {
    defaultSourcesInputRef.current = { reportSections, highSecurity };
  }, [reportSections, highSecurity]);

  const buildDefaultDataSources = useCallback(
    () =>
      defaultTextSectionDataSources({
        reportSections: defaultSourcesInputRef.current.reportSections,
        currentSectionId: section?.id,
        highSecurity: defaultSourcesInputRef.current.highSecurity,
      }),
    [section?.id]
  );

  const handleDataSourcesChange = useCallback(
    (newSelections: unknown) => {
      if (typeof newSelections === 'function') {
        const currentDataSources = getValues('dataSources') || [];
        setValue(
          'dataSources',
          (newSelections as (prev: unknown[]) => unknown[])(currentDataSources),
          { shouldDirty: true }
        );
      } else {
        setValue('dataSources', newSelections as unknown[], {
          shouldDirty: true,
        });
      }
    },
    [setValue, getValues]
  );

  const applyType = useCallback(
    (newType: SectionType) => {
      if (isAutomatedSection) return;
      setValue('type', newType, { shouldDirty: true });
      if (newType === 'ai') {
        const currentInstructions = getValues('ai_instructions');
        if (!currentInstructions || currentInstructions.trim() === '') {
          setValue(
            'ai_instructions',
            getDefaultAiInstructionsForSection({
              sectionName: getValues('name') || section?.name,
              outputFormat: section?.output_format,
              sitrepTemplateId,
            }),
            { shouldDirty: true }
          );
        }
        const currentSources = getValues('dataSources');
        if (!Array.isArray(currentSources) || currentSources.length === 0) {
          setValue('dataSources', buildDefaultDataSources(), {
            shouldDirty: true,
          });
        }
      }
    },
    [
      buildDefaultDataSources,
      getValues,
      isAutomatedSection,
      section?.name,
      section?.output_format,
      setValue,
      sitrepTemplateId,
    ]
  );

  useEffect(() => {
    if (section) {
      const summaryOptions =
        section?.output_format_structured?.summary_options || [];
      const sectionType = (section.type as SectionType) || 'ai';
      const effectiveSources = resolveEffectiveDataSources(
        section.data_sources || [],
        parentDataSources
      );
      reset({
        id: section.id || '',
        name: section.name || '',
        type: sectionType,
        dataSources:
          sectionType === 'ai' && effectiveSources.length === 0
            ? buildDefaultDataSources()
            : effectiveSources,
        ai_instructions: section.ai_instructions || '',
        outputFormats: summaryOptions,
        action_count: clampOpordTasksActionCount(
          section?.output_format_structured?.action_count
        ),
        autoRefreshMinutes: Number(
          section?.output_format_structured?.auto_refresh?.intervalMinutes
        )
          ? Number(
              section.output_format_structured?.auto_refresh?.intervalMinutes
            )
          : 0,
        aiTasksEnabled:
          section?.output_format_structured?.ai_tasks?.enabled === true,
        aiTasksDataSources:
          section?.output_format_structured?.ai_tasks?.data_sources || [],
        aiTasksInstructions:
          section?.output_format_structured?.ai_tasks?.instructions || '',
        aiTasksActionCount: clampOpordTasksActionCount(
          section?.output_format_structured?.ai_tasks?.action_count
        ),
        taskFilter: readTaskSectionFilter(section?.output_format_structured, {
          isOpord: isTasksSection,
        }),
      });
    }
  }, [
    section,
    parentDataSources,
    reset,
    isTasksSection,
    buildDefaultDataSources,
  ]);

  const willRefreshAi = (formData: SectionFormValues) => {
    const isExecutionParent = isOpordExecutionParentSection({
      sectionName: formData.name || section?.name,
      sectionTemplateId: section?.section_template_id,
      parentSectionId: section?.parent_section_id,
    });
    return (
      !!sitrepId &&
      !!formData.id &&
      (formData.type === 'ai' || isExecutionParent)
    );
  };

  const handleSaveSectionSettings = async (formData: SectionFormValues) => {
    const summary_options = formData.outputFormats || [];
    const isAutomated = formData.type === 'automated';
    const existingAutomation =
      section?.automation ||
      section?.output_format_structured?.automation ||
      null;
    const keepLayersOnAutomated =
      isAutomated &&
      (isLayerStatsAutomated ||
        existingAutomation?.provider === 'layerStats' ||
        existingAutomation?.provider === 'orgEvents' ||
        existingAutomation?.provider === 'incidents' ||
        existingAutomation?.provider === 'actions' ||
        existingAutomation?.provider === 'childAorReport');
    const automation = isAutomated
      ? existingAutomation ||
        (keepLayersOnAutomated
          ? { provider: 'layerStats' }
          : {
              provider: 'seerist',
              seeristRequest: DEFAULT_SEERIST_REQUEST,
            })
      : undefined;

    const existingStructured = section?.output_format_structured || {};
    const tasksSection =
      isOpordTasksSection({
        sectionName: formData.name || section?.name,
        outputFormat: section?.output_format,
        sitrepTemplateId,
      }) || isTasksSection;

    const sectionData: SaveSectionPayload = {
      id: formData.id,
      name: formData.name,
      type: formData.type,
      data_sources:
        isAutomated && !keepLayersOnAutomated
          ? []
          : (formData.dataSources as Record<string, unknown>[]),
      ai_instructions: isAutomated ? '' : formData.ai_instructions,
      automation,
      output_format_structured: {
        ...existingStructured,
        summary_options: isAutomated ? [] : summary_options,
        ...(automation ? { automation } : { automation: undefined }),
        ...(tasksSection
          ? {
              action_count: clampOpordTasksActionCount(formData.action_count),
            }
          : {}),
        ...(isActionsAutomated || tasksSection
          ? { task_filter: formData.taskFilter }
          : {}),
        ...(isActionsAutomated
          ? {
              ai_tasks: buildAiTasksSettings({
                enabled: formData.aiTasksEnabled,
                dataSources: formData.aiTasksDataSources,
                instructions: formData.aiTasksInstructions,
                actionCount: formData.aiTasksActionCount,
              }),
            }
          : {}),
        auto_refresh:
          formData.type === 'ai' && formData.autoRefreshMinutes > 0
            ? {
                enabled: true,
                intervalMinutes: formData.autoRefreshMinutes,
              }
            : { enabled: false },
      },
    };
    if (!sectionData.output_format_structured.automation) {
      delete sectionData.output_format_structured.automation;
    }

    await onSave(sectionData);

    const previousMinutes = Number(
      section?.output_format_structured?.auto_refresh?.intervalMinutes
    )
      ? Number(section?.output_format_structured?.auto_refresh?.intervalMinutes)
      : 0;
    const frequencyOnly =
      formData.name === (section?.name || '') &&
      formData.type === ((section?.type as SectionType) || 'ai') &&
      formData.ai_instructions === (section?.ai_instructions || '') &&
      formData.autoRefreshMinutes !== previousMinutes;

    if (willRefreshAi(formData) && !frequencyOnly) {
      refreshSitrepMutation.mutate({
        id: sitrepId as string,
        selectedDatetime: new Date().toISOString(),
        sections: [formData.id],
      });
    }
  };

  const requestClose = () => {
    if (isDirty) {
      setShowDirtyClose(true);
      return;
    }
    onHide();
  };

  const showMoveMenu = layoutMode === 'tabs' && (onMoveUp || onMoveDown);

  return (
    <SheetDrawerWrapper
      toggle={requestClose}
      title="Edit Section Settings"
      description="Change the section name, type, and how content is generated."
      className="tw:[--drawer-inset:0px] tw:[--drawer-content-height:100dvh] tw:!m-0 tw:!h-dvh tw:max-h-dvh"
    >
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(handleSaveSectionSettings)}
          className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col"
        >
          <SheetDrawerBody>
            <input type="hidden" {...register('id')} />
            <FieldGroup className="tw:gap-4! tw:[&_[data-slot=field]]:gap-2!">
              <Field>
                <FieldLabel htmlFor="section-name">Section Name</FieldLabel>
                <Input
                  id="section-name"
                  {...register('name', {
                    required: 'Section name is required',
                  })}
                />
              </Field>

              <Field>
                {isAutomatedSection ? (
                  <div>
                    <span className="tw:font-semibold">Automated</span>
                    <FieldDescription>
                      {describeAutomatedProvider(automatedProvider)}
                    </FieldDescription>
                  </div>
                ) : (
                  <div className="tw:flex tw:items-start tw:gap-2">
                    <Controller
                      control={control}
                      name="type"
                      render={({ field }) => (
                        <Switch
                          id="section-ai-enabled"
                          checked={field.value === 'ai'}
                          className="tw:mt-0"
                          onCheckedChange={(checked) => {
                            applyType(checked === true ? 'ai' : 'manual');
                          }}
                        />
                      )}
                    />
                    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
                      <Label
                        htmlFor="section-ai-enabled"
                        className="tw:leading-5"
                      >
                        AI
                      </Label>
                      <FieldDescription>
                        Generated from selected data sources.
                      </FieldDescription>
                    </div>
                  </div>
                )}
              </Field>

              {isLayerStatsAutomated && (
                <Field>
                  <DataSources
                    selections={dataSources}
                    setSelections={handleDataSourcesChange}
                    usingSitrep={true}
                    hiddenSources={SECTION_SETTINGS_HIDDEN_SOURCES}
                    hideCustomSources
                  />
                </Field>
              )}

              {isActionsAutomated || isTasksSection ? (
                <Controller
                  control={control}
                  name="taskFilter"
                  render={({ field }) => (
                    <TaskSectionFilterFields
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              ) : null}

              {isActionsAutomated && (
                <>
                  <Field>
                    <div className="tw:flex tw:items-start tw:gap-2">
                      <Controller
                        control={control}
                        name="aiTasksEnabled"
                        render={({ field }) => (
                          <Switch
                            id="ai-tasks-enabled"
                            checked={field.value === true}
                            className="tw:mt-0"
                            onCheckedChange={(checked) => {
                              const enabled = checked === true;
                              field.onChange(enabled);
                              if (enabled) {
                                const current = getValues(
                                  'aiTasksInstructions'
                                );
                                if (!current || current.trim() === '') {
                                  setValue(
                                    'aiTasksInstructions',
                                    DEFAULT_AI_TASK_INSTRUCTIONS,
                                    { shouldDirty: true }
                                  );
                                }
                              }
                            }}
                          />
                        )}
                      />
                      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
                        <Label
                          htmlFor="ai-tasks-enabled"
                          className="tw:leading-5"
                        >
                          Generate tasks with AI
                        </Label>
                        <FieldDescription>
                          Optional. When you click Generate tasks with AI in the
                          section, creates Planned tasks from the context you
                          select: incident tasks on an incident report, org
                          tasks otherwise. Saving does not create tasks.
                        </FieldDescription>
                      </div>
                    </div>
                  </Field>
                  {aiTasksEnabled ? (
                    <>
                      <Field>
                        <DataSources
                          selections={aiTasksDataSources}
                          setSelections={(newSelections: unknown) => {
                            if (typeof newSelections === 'function') {
                              const current =
                                getValues('aiTasksDataSources') || [];
                              setValue(
                                'aiTasksDataSources',
                                (
                                  newSelections as (
                                    prev: unknown[]
                                  ) => unknown[]
                                )(current),
                                { shouldDirty: true }
                              );
                            } else {
                              setValue(
                                'aiTasksDataSources',
                                newSelections as unknown[],
                                { shouldDirty: true }
                              );
                            }
                          }}
                          usingSitrep={true}
                          hiddenSources={SECTION_SETTINGS_HIDDEN_SOURCES}
                          hideCustomSources
                        />
                      </Field>
                      {reportSections && reportSections.length > 1 ? (
                        <Field>
                          <FieldLabel>Other sections in this report</FieldLabel>
                          <FieldDescription>
                            Their current text is given to the AI as context for
                            the tasks it proposes.
                          </FieldDescription>
                          <ReportSectionSourcesPicker
                            sections={reportSections}
                            currentSectionId={section?.id}
                            value={aiTasksDataSources}
                            onChange={(next) =>
                              setValue('aiTasksDataSources', next, {
                                shouldDirty: true,
                              })
                            }
                          />
                        </Field>
                      ) : null}
                      <Field>
                        <FieldLabel htmlFor="ai-tasks-instructions">
                          Task instructions
                        </FieldLabel>
                        <Textarea
                          id="ai-tasks-instructions"
                          {...register('aiTasksInstructions')}
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="ai-tasks-action-count">
                          Tasks to generate
                        </FieldLabel>
                        <FieldDescription>
                          Number of Planned tasks to create (
                          {MIN_OPORD_TASKS_ACTION_COUNT}–
                          {MAX_OPORD_TASKS_ACTION_COUNT}).
                        </FieldDescription>
                        <Input
                          id="ai-tasks-action-count"
                          type="number"
                          min={MIN_OPORD_TASKS_ACTION_COUNT}
                          max={MAX_OPORD_TASKS_ACTION_COUNT}
                          {...register('aiTasksActionCount', {
                            valueAsNumber: true,
                            min: MIN_OPORD_TASKS_ACTION_COUNT,
                            max: MAX_OPORD_TASKS_ACTION_COUNT,
                          })}
                        />
                      </Field>
                    </>
                  ) : null}
                </>
              )}

              {selectedType === 'ai' && (
                <>
                  <Field>
                    <DataSources
                      selections={dataSources}
                      setSelections={handleDataSourcesChange}
                      usingSitrep={true}
                      hiddenSources={SECTION_SETTINGS_HIDDEN_SOURCES}
                      hideCustomSources
                    />
                  </Field>
                  {reportSections && reportSections.length > 1 ? (
                    <Field>
                      <FieldLabel>Other sections in this report</FieldLabel>
                      <FieldDescription>
                        Their current text is given to the AI as context. When
                        the whole report is generated, these run first.
                      </FieldDescription>
                      <ReportSectionSourcesPicker
                        sections={reportSections}
                        currentSectionId={section?.id}
                        value={dataSources}
                        onChange={(next) =>
                          setValue('dataSources', next, { shouldDirty: true })
                        }
                      />
                    </Field>
                  ) : null}
                  <Field>
                    <FieldLabel htmlFor="ai_instructions-textarea">
                      AI Instructions
                    </FieldLabel>
                    <FieldDescription>
                      AI interprets and generates search queries
                    </FieldDescription>
                    <Textarea
                      id="ai_instructions-textarea"
                      {...register('ai_instructions')}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="ai-auto-refresh">
                      Auto-update frequency
                    </FieldLabel>
                    <FieldDescription>
                      While this report is open, regenerate the section on this
                      interval.
                    </FieldDescription>
                    <Controller
                      control={control}
                      name="autoRefreshMinutes"
                      render={({ field }) => (
                        <ToggleGroup
                          variant="outline"
                          size="sm"
                          multiple={false}
                          className="tw:w-full tw:max-w-full tw:flex-wrap"
                          value={[String(field.value || 0)]}
                          onValueChange={(next) => {
                            const minutes = Number(next[0]);
                            field.onChange(
                              AUTO_REFRESH_OPTIONS.includes(
                                minutes as (typeof AUTO_REFRESH_OPTIONS)[number]
                              )
                                ? minutes
                                : 0
                            );
                          }}
                          aria-label="Auto-update frequency"
                        >
                          <ToggleGroupItem value="0">Off</ToggleGroupItem>
                          <ToggleGroupItem value="5">5 min</ToggleGroupItem>
                          <ToggleGroupItem value="15">15 min</ToggleGroupItem>
                          <ToggleGroupItem value="30">30 min</ToggleGroupItem>
                          <ToggleGroupItem value="60">60 min</ToggleGroupItem>
                        </ToggleGroup>
                      )}
                    />
                  </Field>
                  <Field>
                    <FieldLabel>Output Format</FieldLabel>
                    <FieldDescription>
                      Select at least one, or use the AI prompt
                    </FieldDescription>
                    <Controller
                      control={control}
                      name="outputFormats"
                      render={({ field }) => {
                        const selected = Array.isArray(field.value)
                          ? field.value
                          : [];
                        return (
                          <div
                            role="group"
                            aria-label="Output format"
                            className="tw:flex tw:min-w-0 tw:flex-row tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-2"
                          >
                            {['Paragraph', 'Table', 'List'].map((option) => {
                              const id = `output-format-${option.toLowerCase()}`;
                              return (
                                <div
                                  key={option}
                                  className="tw:flex tw:w-auto tw:shrink-0 tw:flex-row tw:items-center tw:gap-2"
                                >
                                  <Checkbox
                                    id={id}
                                    checked={selected.includes(option)}
                                    onCheckedChange={(checked) => {
                                      field.onChange(
                                        checked === true
                                          ? [...selected, option]
                                          : selected.filter(
                                              (value) => value !== option
                                            )
                                      );
                                    }}
                                  />
                                  <Label
                                    htmlFor={id}
                                    className="tw:w-auto tw:font-normal"
                                  >
                                    {option}
                                  </Label>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }}
                    />
                  </Field>
                </>
              )}

              {isTasksSection && (
                <Field>
                  <FieldLabel htmlFor="tasks-action-count">
                    Actions to generate
                  </FieldLabel>
                  <FieldDescription>
                    Number of Planned incident actions AI should create on
                    generate ({MIN_OPORD_TASKS_ACTION_COUNT}–
                    {MAX_OPORD_TASKS_ACTION_COUNT}).
                  </FieldDescription>
                  <Input
                    id="tasks-action-count"
                    type="number"
                    min={MIN_OPORD_TASKS_ACTION_COUNT}
                    max={MAX_OPORD_TASKS_ACTION_COUNT}
                    {...register('action_count', {
                      valueAsNumber: true,
                      min: MIN_OPORD_TASKS_ACTION_COUNT,
                      max: MAX_OPORD_TASKS_ACTION_COUNT,
                    })}
                  />
                </Field>
              )}
            </FieldGroup>
          </SheetDrawerBody>

          <SheetDrawerFooter>
            {willRefreshAi(getValues()) ? (
              <span className="tw:text-xs tw:text-muted-foreground">
                Saving will regenerate this AI section.
              </span>
            ) : null}
            <div className="tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2">
              <div className="tw:flex tw:items-center tw:gap-2">
                {onDelete && (
                  <Button
                    type="button"
                    variant="destructive-outline"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete Section
                  </Button>
                )}
                {showMoveMenu ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <Button type="button" variant="outline" size="sm">
                          Move
                        </Button>
                      }
                    />
                    <DropdownMenuContent className="dark">
                      {onMoveUp ? (
                        <DropdownMenuItem
                          disabled={!canMoveUp}
                          onClick={onMoveUp}
                        >
                          <ArrowUp />
                          Move Up
                        </DropdownMenuItem>
                      ) : null}
                      {onMoveDown ? (
                        <DropdownMenuItem
                          disabled={!canMoveDown}
                          onClick={onMoveDown}
                        >
                          <ArrowDown />
                          Move Down
                        </DropdownMenuItem>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </div>
              <div className="tw:flex tw:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={requestClose}
                >
                  Cancel
                </Button>
                <Button type="submit" size="sm">
                  Save
                </Button>
              </div>
            </div>
          </SheetDrawerFooter>
        </form>
      </FormProvider>

      <ConfirmDeleteDialog
        open={showDeleteConfirm}
        title="Delete Section"
        onOpenChange={setShowDeleteConfirm}
        onConfirm={() => {
          onDelete?.();
          setShowDeleteConfirm(false);
          onHide();
        }}
        description={`Are you sure you want to delete “${section?.name}”? This action cannot be undone.`}
      />
      <ConfirmDeleteDialog
        open={showDirtyClose}
        title="Discard unsaved changes?"
        confirmLabel="Discard"
        onOpenChange={setShowDirtyClose}
        onConfirm={() => {
          setShowDirtyClose(false);
          onHide();
        }}
        description="You have unsaved section settings. Closing now discards those edits."
      />
    </SheetDrawerWrapper>
  );
};

export default SitrepSectionEditDrawer;
